import React, { useState, useRef, useEffect } from 'react';
import { Camera } from 'lucide-react';

interface ProfileHeaderProps {
  user: {
    id: number;
    full_name: string;
    email: string;
    role: string;
    avatar_url?: string | null;
    birth_date?: string | null;
  };
  onUpdateAvatar: (data: { avatar_url: string }) => void;
}

export default function ProfileHeader({ user, onUpdateAvatar }: ProfileHeaderProps) {
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user.avatar_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAvatarPreview(user.avatar_url || null);
  }, [user.avatar_url]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('File harus berupa gambar');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Ukuran file maksimal 2MB');
      return;
    }

    setUploading(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setAvatarPreview(result);
        onUpdateAvatar({ avatar_url: result });
        setUploading(false);
      };
      reader.readAsDataURL(file);

      // TODO: Upload to server
      // const formData = new FormData();
      // formData.append('avatar', file);
      // const response = await apiClient.post('/user/avatar', formData);
      // onUpdateAvatar({ avatar_url: response.avatar_url });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Gagal mengunggah foto profil');
      setUploading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (d?: string | null) => {
    if (!d) return null;

    // If value is a simple date string YYYY-MM-DD, avoid creating a Date object
    // because `new Date('YYYY-MM-DD')` is treated as UTC and may shift the day
    // when displayed in local timezone. Instead, format directly from the parts.
    const dateOnlyMatch = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return `${day}/${month}/${year}`;
    }

    // If the string contains a T (ISO datetime), extract the date portion first.
    const isoDateMatch = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) {
      const [datePart] = isoDateMatch;
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    }

    // Fallback: try parsing as a Date and format via locale (best effort)
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    try {
      return dt.toLocaleDateString('id-ID');
    } catch {
      return dt.toISOString().split('T')[0];
    }
  };

  const birthDateFormatted = formatDate(user.birth_date || null);

  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
        {/* Avatar */}
        <div className="relative">
          <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt={user.full_name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(user.full_name)
            )}
          </div>
          <button
            onClick={handleAvatarClick}
            disabled={uploading}
            className="absolute bottom-0 right-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Camera className="w-5 h-5" />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </div>

        {/* User Info */}
        <div className="flex-1 text-center md:text-left">
          <h2 className="text-2xl font-bold text-gray-900">{user.full_name}</h2>
          <p className="text-gray-600 mt-1">{user.email}</p>
          {birthDateFormatted && (
            <p className="text-gray-600 mt-1 text-sm">Tanggal Lahir: {birthDateFormatted}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3 justify-center md:justify-start">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              {user.role}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
